import React, { useEffect, useState } from "react";
import moment from "moment";
import { Box, useTheme } from "@material-ui/core";
import _ from "lodash";
import { useRouteMatch } from "react-router";

import withAuthority from "../../../../components/Auth/withAuthority";
import Authorities from "../../../../auth/authorities";
import DefaultAlert from "../../../../components/alerts/DefaultAlert";
import { ERROR_MESSAGE_UNEXPECTED_ERROR } from "../../../../utils/consts";
import { fetchAllFilterLocations } from "../../../../services/locationApp/locationFilterService";
import { getCookie } from "../../../../utils/cookies";
import { paymentFilterObject } from "../../../../utils/consts/list";
import { CustomTheme } from "../../../../types/customTheme";
import SaleReport from "../../../../components/common/SaleReport";
import {
  getFilterListFromArrayObject,
  getLabelListFromArrayObjectAndArray,
} from "../../../../utils/commonArrayMap";
import DailySaleReportInfoNode from "./DailySaleReportInfoNode";
import { fetchAllDailySaleReportInfo } from "../../../../services/salesApp/dailySaleReportService";
import {
  convertDateFormat,
  convertDateToMoment,
  getEndDateDateMonthYear,
  getStartDateDateMonthYear,
} from "../../../../utils/ConvertDateTimeFormat";
import { fetchAllSaleByLocationReportInfo } from "../../../../services/salesApp/salesByLocationReportService";

export interface DailySaleReportProps {}

type SalesByLocationReport = {
  orders: number;
  subTotal: number;
  discount: number;
  total: number;
  cash: number;
  card: number;
  otherPayment: number;
  average: number;
  min: number;
  max: number;
};

type DailySaleObject = {
  date: string;
  orders: number;
  subTotal: number;
  discount: number;
  total: number;
  cash: number;
  card: number;
  otherPayment: number;
  average: number;
  min: number;
  max: number;
};

/**
 * Component for rendering the Daily Sale Report.
 * This component fetches and displays daily sale report data based on the selected location and date range.
 */
const SalesByLocationReport: React.FunctionComponent<
  DailySaleReportProps
> = () => {
  const [dailySaleReportNodeList, setDailySaleReportNodeList] = useState<any>(
    [],
  );
  const [previousDailySaleReportNodeList, setPreviousDailySaleReportNodeList] =
    useState<any>([]);
  const [error, setError] = useState("");
  const [locationSelectorList, setLocationSelectorList] = useState([]);
  const [isGetLocationInfo, setIsGetLocationInfo] = useState(false);
  const [isShowButton, setIsShowButton] = useState(false);
  const [filterDetails, setFilterDetails] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [openFilterCard, setOpenFilterCard] = useState(false);
  const [locationSelectedList, setLocationSelectedList] = useState([]);
  const [filterData, setFilterData] = useState("");
  const [basedOnShift, setBasedOnShift] = useState(false);
  const [isInitial, setIsInitial] = useState(true);

  const [previousStartDate, setPreviousStartDate] = React.useState<any>(
    moment().subtract(13, "d").format("YYYY-MM-DD"),
  );
  const [previousEndDate, setPreviousEndDate] = React.useState<any>(
    moment().subtract(7, "d").format("YYYY-MM-DD"),
  );
  const [currentStartDate, setCurrentStartDate] = React.useState<any>(
    moment().subtract(6, "d").format("YYYY-MM-DD"),
  );
  const [currentEndDate, setCurrentEndDate] = React.useState<any>(
    moment().format("YYYY-MM-DD"),
  );

  const match: any = useRouteMatch();
  const idToken = getCookie("idToken");

  /**
   * This function takes in a date filter and a daily sale report object and returns an array
   * of daily sale objects within the date range of the filter.
   * @param {string} filterData - A date filter string in the format of "date>=start_date,date<=end_date".
   * @param {object} dailySaleReportObject - An object containing daily sale reports.
   * @returns {array} An array of daily sale objects.
   */
  const handleDailyReportNotIncludeDate = (
    filterData: string,
    dailySaleReportObject: any,
  ) => {
    // Extract location IDs from the filterData string using regex
    const match = filterData.match(/locationId!\(([^)]*)\)/);
    if (!match) {
      return [];
    }

    const locationIds = match[1].split("*");
    const updatedSales = locationIds.map((locationId) => {
      const date =
        getLabelListFromArrayObjectAndArray(
          locationSelectorList,
          locationId,
        )?.[0] ?? "";

      const saleData = dailySaleReportObject[locationId];

      return {
        date,
        ...(saleData ?? {
          average: 0,
          cancelledCount: 0,
          cancelledTotal: 0,
          card: 0,
          cash: 0,
          discount: 0,
          orders: 0,
          otherPayment: 0,
          total: 0,
          voidCount: 0,
          voidTotal: 0,
        }),
      };
    });

    // Sort the results by location name
    const sortedUpdatedSales = updatedSales.sort((a, b) =>
      a.date.localeCompare(b.date),
    );

    return sortedUpdatedSales;
  };

  const getDailySaleReportInfoMax = async (filter: string, basedOnShift: boolean) => {
  const matchLocations = filter.match(/locationId!\(([^)]*)\)/);
  if (!matchLocations) return;

  const allLocationIds = matchLocations[1].split("*");
  const batchSize = 15;
  const totalBatches = Math.ceil(allLocationIds.length / batchSize);

  const allSalesData: any[] = [];

  setIsLoading(true);
  try {
    for (let i = 0; i < totalBatches; i++) {
      const batchLocationIds = allLocationIds.slice(i * batchSize, (i + 1) * batchSize);

      // Construct a new filter for the current batch
      const batchFilter = filter.replace(
        /locationId!\(([^)]*)\)/,
        `locationId!(${batchLocationIds.join("*")})`
      );

      const res = await fetchAllSaleByLocationReportInfo(
        idToken,
        match.params.locationId,
        batchFilter,
        basedOnShift,
      );

      const processedBatchData = handleDailyReportNotIncludeDate(
        batchFilter,
        res.data || {},
      );

      allSalesData.push(...processedBatchData);
    }

    // Sort final combined data by location name
    const finalSortedData = allSalesData.sort((a, b) =>
      a.date.localeCompare(b.date)
    );

    setDailySaleReportNodeList(finalSortedData);
    setIsInitial(false);
    setIsLoading(false);
  } catch (err) {
    setIsLoading(false);
    setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
  }
};

  /** This function retrieves the daily sales report data and
   * updates the state of the component with the updated report
   * The function accepts two parameters: filter and
   * basedOnShift which are used as query parameters when fetching the data
   */
  const getDailySaleReportInfo = async (filter: any, basedOnShift: boolean) => {
    setIsLoading(true);
    try {
      // This function calls the fetchAllDailySaleReportInfo API to retrieve the data
      const res = await fetchAllSaleByLocationReportInfo(
        idToken,
        match.params.locationId,
        filter,
        basedOnShift,
      );
      // If the response data is not empty, the function calls the handleDailyReportNotIncludeDate function to process the data
      if (!_.isEmpty(res.data)) {
        const sortedUpdatedSales = handleDailyReportNotIncludeDate(
          filter,
          res.data,
        );

        // The updated daily sale data is set to the dailySaleReportNodeList state
        setDailySaleReportNodeList(sortedUpdatedSales);
      } else {
        const sortedUpdatedSales = handleDailyReportNotIncludeDate(filter, {});

        // The updated daily sale data is set to the dailySaleReportNodeList state
        setDailySaleReportNodeList(sortedUpdatedSales);
      }
      setIsInitial(false);
      // The setIsLoading state is set to false to stop the loading spinner
      setIsLoading(false);
    } catch (err) {
      // If an error occurs, the setIsLoading state is set to false and the setError state is set to an error message
      setIsLoading(false);
      setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
    }
  };

  /** A function that fetches all filter locations based on searchName and updates the state
   * of locationList, locationData, locationSelectorList and isGetLocationInfo based on the response
   * */
  const getAllFilterLocation = async (searchName: any) => {
    fetchAllFilterLocations(searchName)
      .then((res) => {
        let locationList: any = [];
        /* Setting up the list of locations as needed to select locations. */
        if (!_.isEmpty(res.data.data)) {
          res.data.data.map((location: any) => {
            locationList.push({
              id: location.id,
              label: location.businessDisplayName,
            });
          });
        }
        setLocationSelectorList(locationList);
        setIsGetLocationInfo(true);
      })
      .catch(() => {
        setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
      });
  };

  useEffect(() => {
    document.title = "Sale - Sales by Locations";
    getAllFilterLocation("");
  }, []);

  // Get filter data
  const handleFilterData = (filterData: any, basedOnShift: boolean) => {
    const locationIds =
      filterData.match(/locationId!\(([^)]*)\)/)?.[1].split("*") ?? [];
    if (locationIds.length < 11) {
      getDailySaleReportInfo(filterData, basedOnShift);
      setIsShowButton(false);
    } else {
      setFilterData(filterData);
      setBasedOnShift(basedOnShift);
      setIsShowButton(true);
      setIsLoading(false);
    }
  };

  const handlePreviousFilterData = (
    filterData: any,
    basedOnShift: boolean,
  ) => {};

  const handleOnClickText = () => {
    setOpenFilterCard(true);
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  /*Get location list API call after typing. */
  const handleLocationSelectorTypingList = (searchName: any) => {};

  const theme: CustomTheme = useTheme();

  return (
    <>
      <Box>
        <SaleReport
          handleFilterData={handleFilterData}
          locationSelectorList={locationSelectorList}
          handleLocationSelectorTypingList={handleLocationSelectorTypingList}
          isGetLocationInfo={isGetLocationInfo}
          setFilterDetails={setFilterDetails}
          availableFilter={paymentFilterObject}
          isOpenSkeletonLoading={isLoading}
          nodeList={dailySaleReportNodeList}
          filterDetails={filterDetails}
          title={""}
          isChart={true}
          filterTitle="Sales by Locations for"
          topic="Sales by Locations"
          isNeedFilterDetails={true}
          getPreviousFilterInfo={handlePreviousFilterData}
          setPreviousStartDate={setPreviousStartDate}
          setPreviousEndDate={setPreviousEndDate}
          setCurrentStartDate={setCurrentStartDate}
          setCurrentEndDate={setCurrentEndDate}
          previousStartDate={previousStartDate}
          previousEndDate={previousEndDate}
          isDailySale={true}
          locationSelectedList={locationSelectedList}
          setLocationSelectedList={setLocationSelectedList}
          openFilterCard={openFilterCard}
          setOpenFilterCard={setOpenFilterCard}
          handleOnClickText={handleOnClickText}
          isReport={true}
          isWeekDateRange={true}
          filterData={filterData}
          basedOnShift={basedOnShift}
          getDailySaleReportInfo={getDailySaleReportInfo}
          isNotNotFound={true}
          isYesterdayDateRange={true}
        >
          <DailySaleReportInfoNode
            dailySaleReportNodeList={dailySaleReportNodeList}
            filterDetails={filterDetails}
            previousDailySaleReportNodeList={previousDailySaleReportNodeList}
            locationSelectedList={locationSelectedList}
            handleOnClickText={handleOnClickText}
            locationSelectorList={locationSelectorList}
            getDailySaleReportInfo={getDailySaleReportInfoMax}
            filterData={filterData}
            isBasedOnShift={basedOnShift}
            isShowButton={isShowButton}
            isNotNotFound={true}
          />
        </SaleReport>

        <DefaultAlert
          open={!!error}
          handleClose={() => setError("")}
          message={error}
          severity="error"
        />
      </Box>
    </>
  );
};

export default withAuthority(SalesByLocationReport, Authorities.SALE_READ);
